import { Fragment } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from 'context/AuthContext.jsx';
import { useTheme } from 'context/ThemeContext.jsx';
import { useDashData } from 'context/DashDataContext.jsx';
import UpdatesBanner from 'components/UpdatesBanner.jsx';
import WeekGate from 'components/WeekGate.jsx';
import NotificationBell from 'components/NotificationBell.jsx';
import { NAV_ITEMS } from './navigation.js';
import { fmt } from 'lib/format.mjs';

import Ticker from './Ticker.jsx';
import { Button } from 'components/common';
/**
 * The Jarvis shell — ticker across the top, icon rail down the left.
 *
 * #v3-shell is position:fixed, inset:0, with an OPAQUE background at z-index 10,
 * so it covers the whole viewport when mounted. In public/index.html that made
 * the login page unclickable until it was gated behind body.logged-in; here it
 * simply is not mounted unless the user is signed in, because it lives inside
 * the protected branch of the route table.
 */
export default function V3Shell() {
  const { name, logout, isAdmin } = useAuth();
  const { cycleTheme, bootEnabled, toggleBoot, openOnboarding } = useTheme();
  const { safeSummary, campaigns, weekLabel, loading } = useDashData();

  const s = safeSummary;
  const money = v => (v == null || v === '' ? '—' : `$${fmt(v)}`);
  // The summary carries totalSpend / totalRevenue and no order count — reading
  // spend / revenue / purchases left three of the four figures at "—". Orders
  // are summed from the campaigns, the same way the Command view counts them.
  const orders = data => data.reduce((n, c) => n + (parseInt(c.purchases, 10) || 0), 0);

  return (
    <div id="v3-shell">
      <div id="v3-ticker">
        <Ticker label="SPEND"   value={money(s.totalSpend)}   color="#ef4444" />
        <Ticker label="REVENUE" value={money(s.totalRevenue)} color="#10b981" />
        <Ticker label="ROAS"    value={s.roas ? `${parseFloat(s.roas).toFixed(2)}x` : '—'} color="#0ea5e9" />
        <Ticker label="ORDERS"  value={campaigns.length ? orders(campaigns) : '—'} color="#8b5cf6" />
        <Ticker label="WEEK"    value={loading ? '…' : (weekLabel || '—')} color="#94a3b8" small />

        <div className="v3-ticker-spacer" />
        <div className="v3-uplink"><div className="v3-uplink-dot" />UPLINK ACTIVE</div>

        <div className="v3-ticker-controls">
          {/* Ahead of the operative name and the control buttons: it is the only
              thing here that CHANGES on its own, and a badge people are meant to
              notice does not belong at the end of a row of static chrome. */}
          <NotificationBell variant="v3" />
          {name && <span className="v3-operative">{name.toUpperCase()}</span>}
          <Button variant="ctrl" onClick={cycleTheme}>THEME: V3</Button>
          <Button variant="ctrl" onClick={toggleBoot}>BOOT: {bootEnabled ? 'ON' : 'OFF'}</Button>
          <Button
            variant="ctrl"
            onClick={openOnboarding}
            title="Show the interface guide"
            aria-label="Show the interface guide"
          >?</Button>
          <Button variant="eject" onClick={logout}>EJECT</Button>
        </div>
      </div>

      <div id="v3-body">
        <nav id="v3-nav">
          {/* A Fragment, NOT a wrapping <div>. #v3-nav is the flex container, so
              whatever sits directly inside it is what gets laid out along the
              rail's axis. A wrapper made each PAIR the flex item, and the
              separator then stacked in the wrapper's own block direction rather
              than the rail's.

              Down the left rail that happened to look right. On the phone, where
              the rail becomes a horizontal bottom bar, it put the separator
              ABOVE its item instead of beside it — so MISS and LOG, the only two
              carrying one, sat a row lower than the other six. */}
          {NAV_ITEMS.filter(i => i.v3Nav && (!i.adminOnly || isAdmin)).map(item => (
            <Fragment key={item.path}>
              {item.sepBefore && <div className="v3-nav-sep" />}
              <NavLink
                to={item.path}
                title={item.title}
                className={({ isActive }) => `v3-nav-item${isActive ? ' active' : ''}`}
              >
                <div className="v3-nav-icon">{item.icon}</div>
                <div className="v3-nav-lbl">{item.v3Label}</div>
              </NavLink>
            </Fragment>
          ))}
        </nav>

        <div id="v3-content">
          <UpdatesBanner />
          <WeekGate><Outlet /></WeekGate>
        </div>
      </div>
    </div>
  );
}

/**
 * One segment of the top ticker. The colour is per-metric, so it arrives as a
 * custom property rather than a class per metric — every rule lives in
 * layout.css.
 */

